// Libs
import { Button, Flex, Heading, IconArrowEndLine, Img, Spinner } from '@instructure/ui';
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
// Others
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { IAccountLogin } from '~/utils/mockData/common';
import { studentRouteAbsolute } from '~/utils/routes/student';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { createCanvasToken, handleLoginWithUrl } from '~/thunks/auth/authThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { selectLoginUrl } from '~/thunks/auth/authSlice';
// Styles, images, icons
import { images } from '~/assets';
import styles from './SignIn.module.scss';

const cx = classNames.bind(styles);

const SignIn = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/auth', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { handleSubmit } = useForm<IAccountLogin>({});
  //#endregion Declare Hook

  //#region Selector
  const loginUrl = useAppSelector(selectLoginUrl);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    handleGetLoginUrl();
    handleCreateCanvasToken();
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitForm = (data: IAccountLogin) => {
    if (loginUrl) {
      window.location.href = loginUrl;
    }
  };

  const handleGetLoginUrl = () => {
    dispatch(handleLoginWithUrl()).unwrap();
  };

  const handleCreateCanvasToken = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code') ?? undefined;
      const state = params.get('state') ?? undefined;
      if (!code) return;

      setIsLoading(true);
      const payload = { code, state };
      await dispatch(createCanvasToken(payload))
        .unwrap()
        .then((res) => {
          if (!res.data) return;
          const accountInfo = res.data.account;
          const tokenInfo = res.data.token;

          localStorage.setItem(StorageEnum.ACCESS_TOKEN, tokenInfo.access);
          localStorage.setItem(StorageEnum.REFRESH_TOKEN, tokenInfo.refresh);
          localStorage.setItem(StorageEnum.ROLE, accountInfo.role);
          localStorage.setItem(StorageEnum.ACCOUNT_ID, accountInfo.id);

          switch (accountInfo.role) {
            case RoleEnum.ADMIN:
              navigate(`${adminRouteAbsolute.dashboard}`);
              break;
            case RoleEnum.STUDENT:
              navigate(`${studentRouteAbsolute.dashboard}`);
              break;
          }
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    } catch (e) {}
  };
  //#endregion Handle Function
  return (
    <div id='signInCommonPage' className={cx('container')}>
      <section className={cx('formGroup')}>
        <div className={cx('logo')}>
          <Img
            src={images.commonLogoFullPositive}
            alt={t('common_alt_img', { ns: 'common/base' })}
            constrain='contain'
          />
        </div>

        <Flex direction='column' gap='small'>
          <Heading variant='titleSection'>{t('title_sign_in')}</Heading>
        </Flex>

        <form className={cx('formLogin')} onSubmit={handleSubmit(handleSubmitForm)}>
          <Button type='submit' color='primary' disabled={isLoading}>
            <Flex justifyItems='center' alignItems='center' gap='small'>
              <span>{t('form_login_btn_login')}</span>
              {isLoading ? <Spinner size='x-small' /> : <IconArrowEndLine />}
            </Flex>
          </Button>
        </form>
      </section>
    </div>
  );
};

export default SignIn;
